/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React, { useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import AnswerItem from '../QuestionEditForm/AnswerItem';

// The "Tags" list of the filter editor: one draggable input per response
// label, with an eye toggle to show or hide the tag from users. Answer-path
// tags hide by allowedValue key, label-path tags by dimension id — both are
// persisted in the sample's hiddenAnswers.
const TagList = ({
  tags,
  tagIdOrders,
  hiddenTags,
  onTagChange,
  onHiddenChange,
  onTagIdOrderChange,
  onDirty,
}) => {
  // Derived during render rather than mirrored into state: a useEffect copy
  // lags one render behind the prop and collapses the input caret to the end
  // of the text on every keystroke (same fix as AnswerList/LabelList).
  const tagMap = useMemo(() => {
    const map = {};
    (tags || []).forEach((tag) => {
      map[tag.id] = tag.value;
    });
    return map;
  }, [tags]);

  // hiddenTags holds stringified ids (the backend validates hiddenAnswers as
  // an array of strings), so toggle and match by String(id).
  const handleToggleHidden = (tag) => {
    const hiddenList = [...hiddenTags];
    const hiddenItemIndex = hiddenList.indexOf(String(tag.id));
    if (hiddenItemIndex >= 0) {
      hiddenList.splice(hiddenItemIndex, 1);
    } else {
      hiddenList.push(String(tag.id));
    }
    onHiddenChange(hiddenList);
    onDirty();
  };

  const moveCard = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }
      const list = [...tagIdOrders];
      const [removedItem] = list.splice(result.source.index, 1);
      list.splice(result.destination.index, 0, removedItem);
      onTagIdOrderChange(list);
      onDirty();
    },
    [tagIdOrders, onTagIdOrderChange, onDirty]
  );

  if (!tags || tags.length === 0) return null;

  return (
    <div>
      <h4>
        Tags
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              Edit the display name of each filter tag. Drag a tag to reorder it, or use the eye
              icon to show or hide it from users.
            </Tooltip>
          }
          delay={100}
        >
          <FontAwesomeIcon className="cursor-pointer ml-2" size="1x" icon={faInfoCircle} />
        </OverlayTrigger>
      </h4>
      <DragDropContext onDragEnd={moveCard}>
        <Droppable droppableId="droppable-filter-tags">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {tagIdOrders.map((id, index) => {
                const hiddenTag = hiddenTags.indexOf(String(id)) >= 0;
                return (
                  <Draggable
                    key={index}
                    draggableId={`draggable-filter-tag-${index}`}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <AnswerItem
                        draggableProvided={draggableProvided}
                        answer={{ id, value: tagMap[id] }}
                        index={index}
                        hiddenAnswer={hiddenTag}
                        onAnswerChange={onTagChange}
                        handleToggleHidden={handleToggleHidden}
                        onDirty={onDirty}
                      />
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default TagList;
